import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { AlertTriangle, Flame, Droplets, Wind, Waves, MapPin, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../common/Button';
import { type Incident, type IncidentMapData } from '../../lib/abc-incident-scraper';

// Use production API endpoint - Cloudflare Functions are available at the same domain
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_APP_URL || 'https://portal.idecide.co';

// Fix Leaflet default icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface IncidentMapProps {
  isOpen: boolean;
  onClose: () => void;
}

// Australia center coordinates
const AUSTRALIA_CENTER: [number, number] = [-25.2744, 133.7751];
const DEFAULT_ZOOM = 5;

// Create custom icons for different incident types
function createCustomIcon(type: string, severity: Incident['severity']) {
  const size = 32;
  const severityColors: Record<Incident['severity'], string> = {
    'evacuate-now': '#DC2626',
    'emergency-warning': '#EA580C',
    'watch-and-act': '#F59E0B',
    'advice': '#3B82F6',
    'all-clear': '#10B981',
    'unknown': '#6B7280'
  };

  const typeEmojis: Record<string, string> = {
    'Bushfire': '🔥',
    'Flood': '💧',
    'Cyclone': '🌀',
    'Storm': '⛈️',
    'Tsunami': '🌊',
    'Earthquake': '⚠️'
  };

  const color = severityColors[severity] || severityColors.unknown;
  const emoji = typeEmojis[type] || '⚠️';

  return L.divIcon({
    className: 'custom-incident-icon',
    html: `
      <div style="
        background: ${color};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
      ">
        <div style="
          transform: rotate(45deg);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          width: 100%;
          height: 100%;
        ">
          ${emoji}
        </div>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size]
  });
}

// Component to fit map bounds to show all incidents
function FitBounds({ incidents }: { incidents: Incident[] }) {
  const map = useMap();

  useEffect(() => {
    if (incidents.length === 0) return;

    const validIncidents = incidents.filter(
      (inc) => inc.location.lat && inc.location.lng
    );

    if (validIncidents.length === 0) {
      map.setView(AUSTRALIA_CENTER, DEFAULT_ZOOM);
      return;
    }

    if (validIncidents.length === 1) {
      const inc = validIncidents[0];
      map.setView([inc.location.lat!, inc.location.lng!], 10);
      return;
    }

    const bounds = L.latLngBounds(
      validIncidents.map((inc) => [inc.location.lat!, inc.location.lng!] as [number, number])
    );
    map.fitBounds(bounds, { padding: [50, 50] });
  }, [incidents, map]);

  return null;
}

export function IncidentMap({ isOpen, onClose }: IncidentMapProps) {
  const [mapData, setMapData] = useState<IncidentMapData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedState, setSelectedState] = useState<string>('all');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');

  const loadIncidents = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('🔄 Fetching incident data from API:', `${API_BASE_URL}/api/scrape-abc-incidents`);
      
      const response = await fetch(`${API_BASE_URL}/api/scrape-abc-incidents`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Failed to fetch incidents:', response.status, errorText);
        throw new Error(`Failed to fetch incident data: ${response.status} ${errorText}`);
      }

      const data: IncidentMapData = await response.json();
      console.log('✅ Incident data fetched:', data.totalIncidents, 'incidents');
      
      if (data.totalIncidents === 0) {
        setError('No active incidents found at this time.');
      } else {
        setMapData(data);
      }
    } catch (err: any) {
      console.error('❌ Error loading incidents:', err);
      const errorMessage = err?.message || 'Failed to load incident data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && !mapData) {
      loadIncidents();
    }
  }, [isOpen, mapData]);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when closed
      setMapData(null);
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const allIncidents = mapData?.states.flatMap((state) => state.incidents) || [];
  
  const filteredIncidents = allIncidents.filter((inc) => {
    if (selectedState !== 'all' && inc.location.state !== selectedState) return false;
    if (selectedSeverity !== 'all' && inc.severity !== selectedSeverity) return false;
    if (selectedType !== 'all' && inc.type !== selectedType) return false;
    return true;
  });

  const uniqueTypes = Array.from(new Set(allIncidents.map((inc) => inc.type))).sort();
  const uniqueSeverities: Incident['severity'][] = ['evacuate-now', 'emergency-warning', 'watch-and-act', 'advice', 'all-clear', 'unknown'];
  const uniqueStates = Array.from(new Set(allIncidents.map((inc) => inc.location.state))).sort();

  const severityLabels: Record<Incident['severity'], string> = {
    'evacuate-now': 'Evacuate Now',
    'emergency-warning': 'Emergency Warning',
    'watch-and-act': 'Watch & Act',
    'advice': 'Advice',
    'all-clear': 'All Clear',
    'unknown': 'Unknown'
  };

  const severityColors: Record<Incident['severity'], string> = {
    'evacuate-now': 'bg-red-600',
    'emergency-warning': 'bg-orange-600',
    'watch-and-act': 'bg-yellow-500',
    'advice': 'bg-blue-500',
    'all-clear': 'bg-green-500',
    'unknown': 'bg-gray-500'
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-7xl h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Live Incident Map</h2>
            <p className="text-sm text-gray-600 mt-1">
              Real-time emergency incidents across Australia • Data from ABC Emergency
            </p>
          </div>
          <div className="flex items-center gap-3">
            {mapData && (
              <div className="text-sm text-gray-600">
                <span className="font-semibold">{mapData.totalIncidents}</span> active incidents
              </div>
            )}
            <Button variant="secondary" size="sm" onClick={loadIncidents} disabled={loading}>
              {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              Refresh
            </Button>
            <Button variant="secondary" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Filters Sidebar */}
          <div className="w-64 border-r border-gray-200 bg-gray-50 p-4 overflow-y-auto">
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 block">
                  State/Territory
                </label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-idecide-primary"
                >
                  <option value="all">All States</option>
                  {uniqueStates.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 block">
                  Severity
                </label>
                <div className="space-y-2">
                  {uniqueSeverities.map((severity) => (
                    <button
                      key={severity}
                      onClick={() => setSelectedSeverity(selectedSeverity === severity ? 'all' : severity)}
                      className={`w-full text-left rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        selectedSeverity === severity
                          ? `${severityColors[severity]} text-white`
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {severityLabels[severity]}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 block">
                  Incident Type
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-idecide-primary"
                >
                  <option value="all">All Types</option>
                  {uniqueTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {filteredIncidents.length > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                    Showing {filteredIncidents.length} of {allIncidents.length}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Map */}
          <div className="flex-1 relative">
            {loading && !mapData ? (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                <div className="text-center">
                  <Loader2 size={48} className="animate-spin text-idecide-primary mx-auto mb-4" />
                  <p className="text-gray-600">Loading incident data from ABC Emergency...</p>
                </div>
              </div>
            ) : error ? (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                <div className="text-center">
                  <AlertTriangle size={48} className="text-red-500 mx-auto mb-4" />
                  <p className="text-gray-900 font-semibold mb-2">Failed to load incidents</p>
                  <p className="text-gray-600 mb-4">{error}</p>
                  <Button variant="primary" onClick={loadIncidents}>
                    Retry
                  </Button>
                </div>
              </div>
            ) : mapData && filteredIncidents.length > 0 ? (
              <MapContainer
                center={AUSTRALIA_CENTER}
                zoom={DEFAULT_ZOOM}
                style={{ height: '100%', width: '100%' }}
                className="z-0"
                key="incident-map"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds incidents={filteredIncidents} />
                {filteredIncidents
                  .filter((inc) => inc.location.lat && inc.location.lng)
                  .map((incident) => (
                    <Marker
                      key={incident.id}
                      position={[incident.location.lat!, incident.location.lng!]}
                      icon={createCustomIcon(incident.type, incident.severity)}
                    >
                      <Popup>
                        <div className="p-2 min-w-[200px]">
                          <div className="flex items-start gap-2 mb-2">
                            <div className={`w-3 h-3 rounded-full ${severityColors[incident.severity]}`} />
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900 text-sm">{incident.title}</h3>
                              <p className="text-xs text-gray-500 mt-1">{incident.type}</p>
                            </div>
                          </div>
                          <p className="text-xs text-gray-600 mb-2">{incident.description}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <MapPin size={12} />
                            <span>{incident.location.name}, {incident.location.state}</span>
                          </div>
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <a
                              href={incident.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-idecide-primary hover:underline"
                            >
                              View on ABC →
                            </a>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
              </MapContainer>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                <div className="text-center max-w-md px-4">
                  <MapPin size={48} className="text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-900 font-semibold mb-2">No incidents to display</p>
                  <p className="text-gray-600 text-sm">
                    {error?.includes('CORS') || error?.includes('fetch')
                      ? 'Incident data scraping requires a backend server due to CORS restrictions. Please check back later or visit ABC Emergency directly.'
                      : 'No active incidents found at this time.'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

